import React from "react";
import { CalendarDays, X } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const PRESETS = [
  { label: "Today", getValue: () => { const d = new Date().toISOString().slice(0, 10); return { from: d, to: d }; } },
  { label: "7D", getValue: () => { const t = new Date(); const f = new Date(t); f.setDate(f.getDate() - 7); return { from: f.toISOString().slice(0, 10), to: t.toISOString().slice(0, 10) }; } },
  { label: "30D", getValue: () => { const t = new Date(); const f = new Date(t); f.setDate(f.getDate() - 30); return { from: f.toISOString().slice(0, 10), to: t.toISOString().slice(0, 10) }; } },
  { label: "90D", getValue: () => { const t = new Date(); const f = new Date(t); f.setDate(f.getDate() - 90); return { from: f.toISOString().slice(0, 10), to: t.toISOString().slice(0, 10) }; } },
  { label: "This Month", getValue: () => { const t = new Date(); return { from: new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10), to: t.toISOString().slice(0, 10) }; } },
  { label: "Last Month", getValue: () => { const t = new Date(); const s = new Date(t.getFullYear(), t.getMonth() - 1, 1); const e = new Date(t.getFullYear(), t.getMonth(), 0); return { from: s.toISOString().slice(0, 10), to: e.toISOString().slice(0, 10) }; } },
  { label: "This Quarter", getValue: () => { const t = new Date(); const q = Math.floor(t.getMonth() / 3) * 3; return { from: new Date(t.getFullYear(), q, 1).toISOString().slice(0, 10), to: t.toISOString().slice(0, 10) }; } },
];

const formatDisplay = (from, to) => {
  if (!from && !to) return "All Time";
  const fmt = (d) => {
    const dt = new Date(d + "T00:00:00");
    return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" });
  };
  if (from && to) return `${fmt(from)} - ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  return `Until ${fmt(to)}`;
};

export const DateRangePicker = ({ dateFrom, dateTo, onChange }) => {
  const hasRange = dateFrom || dateTo;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`gap-2 text-xs font-medium ${hasRange ? "border-blue-300 bg-blue-50 text-blue-700" : "text-slate-600"}`}
          data-testid="date-range-picker-trigger"
        >
          <CalendarDays className="w-3.5 h-3.5" />
          {formatDisplay(dateFrom, dateTo)}
          {hasRange && (
            <X
              className="w-3 h-3 ml-1 hover:text-red-500"
              onClick={(e) => { e.stopPropagation(); onChange("", ""); }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="end">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <Button
                key={p.label}
                variant="outline"
                size="sm"
                className="text-xs h-7 px-2.5"
                onClick={() => { const v = p.getValue(); onChange(v.from, v.to); }}
                data-testid={`date-preset-${p.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateFrom || ""}
              onChange={(e) => onChange(e.target.value, dateTo || "")}
              className="flex-1 h-8 px-2 text-xs border rounded-md border-slate-200 bg-white"
              data-testid="date-from-input"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date"
              value={dateTo || ""}
              onChange={(e) => onChange(dateFrom || "", e.target.value)}
              className="flex-1 h-8 px-2 text-xs border rounded-md border-slate-200 bg-white"
              data-testid="date-to-input"
            />
          </div>
          {hasRange && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs h-7 text-slate-500"
              onClick={() => onChange("", "")}
            >
              Clear - Show All Time
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Build a URL to the Leads page with filters applied. Opens in new tab.
 */
export const openLeadsTab = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.stage) params.set("stage", filters.stage);
  if (filters.score) params.set("score", filters.score);
  if (filters.source) params.set("source", filters.source);
  if (filters.sub_source) params.set("sub_source", filters.sub_source);
  if (filters.project) params.set("project", filters.project);
  if (filters.campaign) params.set("campaign", filters.campaign);
  if (filters.assigned_to) params.set("assigned_to", filters.assigned_to);
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  const qs = params.toString();
  window.open(`/leads${qs ? `?${qs}` : ""}`, "_blank");
};
